import { yupResolver } from '@hookform/resolvers/yup'
import { Alert } from '@material-ui/lab'
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  Typography
} from '@material-ui/core'
import Container from '@material-ui/core/Container'
import CssBaseline from '@material-ui/core/CssBaseline'
import { makeStyles } from '@material-ui/core/styles'
import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from 'src/components/ui/Button'
import TextField from 'src/components/ui/TextField'
import yup from 'src/components/yupCustom'
import {
  onlyNumber,
  verificarCNPJ,
  verificarCPF,
  verificarTelefone
} from 'src/core/helpers/common'
import { SynPerfis } from 'src/core/consts/synPerfis'
import useAuth from 'src/hooks/useAuth'
import moment from 'moment'
import UBox from 'src/components/ui/Ubox'
import LoadingGeral from 'src/components/ui/LoadingGeral'
import DateInput from 'src/components/ui/DateInput'
import {
  showNotification,
  showNotificationErrorAPI
} from 'src/core/helpers/notification'
import {
  alterarDadosPaciente,
  ISalvarDadosPacienteRequest,
  IVincularPacienteSolicitacaoRequest,
  obterDadosPacientePorId,
  vincularPacienteSolicitacao
} from 'src/api/paciente'
import genero from 'src/core/consts/genero'
import { ItemListagemSolicitacaoOrcamentoResponse } from 'src/types'
import { obterPacientePorCPF } from 'src/api/dominio'

const useStyles = makeStyles(theme => ({
  inputIcon: { color: theme.palette.grey[500] },
  form: { width: '100%' },
  formControl: {
    minWidth: 250,
    marginTop: 15
  },
  selectEmpty: {
    marginTop: theme.spacing(1)
  },
  subTitle: {
    padding: theme.spacing(3, 0, 0, 0)
  },
  noMargin: {
    margin: theme.spacing(0)
  }
}))

interface IFormDadosPaciente {
  id?: string
  nome: string
  sexo?: number
  email: string
  cpf: string
  dataNascimento: string
  telefone: string
  cpfResponsavel?: string
  nomeResponsavel?: string
  comorbidades?: string[]
  responsavelPacienteId?: string
}

const dadosPacienteDefault = {
  id: '',
  nome: '',
  email: '',
  cpf: '',
  dataNascimento: '',
  telefone: '',
  comorbidades: [],
  responsavelPacienteId: null,
  sexo: 0
}

interface IProps {
  solicitacao: ItemListagemSolicitacaoOrcamentoResponse

  handleAfterSubmit: (atualizou, solicitacao?) => void
}

export default function FormVincularPaciente({
  solicitacao,
  handleAfterSubmit
}: IProps) {
  const schema = yup.object().shape({
    nome: yup
      .string()
      .required()
      .test('nome válido', 'Deve informar nome e sobrenome', value => {
        if (!value) return false
        let nomeArray = value.split(' ').filter(texto => texto.trim())
        return nomeArray.length > 1
      })
      .label('Nome completo'),

    nomeResponsavel: yup
      .string()
      .test('nome-responsavel', 'Nome do responsável é obrigatório', value => {
        if (pacienteResponsavel && !value) {
          return false
        }
        return true
      })
      .test('nome válido', 'Deve informar nome e sobrenome', value => {
        if (pacienteResponsavel) {
          let nomeArray = value.split(' ').filter(texto => texto.trim())

          return nomeArray.length > 1
        }

        return true
      })
      .label('Nome completo'),
    cpf: yup
      .string()
      .nullable()
      .test('cpf-teste', 'CPF inválido', value => {
        if (pacienteResponsavel) return true

        if (!value) return false

        return verificarCPF(onlyNumber(value))
      })
      .label('CPF'),

    cpfResponsavel: yup
      .string()
      .nullable()
      .test(
        'cpf-responsavel',
        'CPF do responsável é obrigatório',
        function (value) {
          if (pacienteResponsavel && !value) {
            return false
          }
          return true
        }
      )
      .test('cpf-teste', 'CPF inválido', value => {
        if (pacienteResponsavel && value) {
          return verificarCPF(onlyNumber(value))
        }
        return true
      })
      .label('CPF'),
    telefone: yup
      .string()
      .required()
      .test('telefone-teste', 'Telefone inválido', value => {
        if (!value) return false
        return verificarTelefone(onlyNumber(value))
      })
      .label('Telefone'),
    dataNascimento: yup
      .string()
      .required()
      .test('nas', 'Por favor escolha uma data válida', value => {
        return moment(value).isBetween('1922-12-31', moment(), 'day')
      })
      .label('Data de nascimento'),
    email: yup.string().email().label('E-mail'),
    sexo: yup
      .number()
      .required()
      .label('Sexo')
      .test('sexo-teste', 'Informe o sexo', value => {
        if (!value) return false
        return true
      })
  })
  const classes = useStyles()
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingPaciente, setLoadingPaciente] = useState(false)

  const [pacienteResponsavel, setPacienteResponsavel] = useState(false)

  const {
    watch,
    register,
    setValue,
    errors,
    control,
    handleSubmit
  } = useForm<IFormDadosPaciente>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: dadosPacienteDefault
  })
  const command = watch()
  useEffect(() => {
    register('id')
    register('nome')
    register('telefone')
    register('dataNascimento')
    register('email')
    register('sexo')
    register('cpfResponsavel')
    register('nomeResponsavel')
    register('cpf')
    register('comorbidades')
    register('responsavelPacienteId')
  }, [register])

  useEffect(() => {
    if (!command.cpf && !command.cpfResponsavel) return

    const cpfFormatado = onlyNumber(
      !!pacienteResponsavel ? command.cpfResponsavel : command.cpf
    )
    if (cpfFormatado.length === 11) {
      setLoadingPaciente(true)
      obterPacientePorCPF(cpfFormatado)
        .then(response => {
          const paciente = response.data

          if (!!paciente && !pacienteResponsavel) {
            setValue('comorbidades', paciente.comorbidades, {
              shouldValidate: true
            })
            setValue('nome', paciente.nome, { shouldValidate: true })
            if (!!paciente.dataNascimento) {
              let dataFormatada = moment(paciente.dataNascimento).format(
                'yyyy-MM-DD'
              )
              setValue('dataNascimento', dataFormatada, {
                shouldValidate: true
              })
            }
            setValue('telefone', paciente.telefone, {
              shouldValidate: true
            })
            setValue('id', paciente.id)
            setValue('sexo', paciente.sexo)
            setValue('email', paciente.email, { shouldValidate: true })
            setValue('responsavelPacienteId', null)
          } else if (!!paciente && pacienteResponsavel) {
            setValue('id', null)
            setValue('cpf', '')
            setValue('nomeResponsavel', paciente.nome)
            setValue('responsavelPacienteId', paciente.id)
          } else {
            limparFormPac(pacienteResponsavel)
          }
        })
        .catch(e => {
          showNotificationErrorAPI(e)
          limparFormPac(pacienteResponsavel)
        })
        .finally(() => {
          setTimeout(() => setLoadingPaciente(false), 100)
        })
    }
  }, [command.cpf, command.cpfResponsavel])

  function limparFormPac(ehResponsavel: boolean) {
    if (ehResponsavel) {
      setValue('nomeResponsavel', '')
      setValue('responsavelPacienteId', null)
      return
    }
    setValue('id', null)
    setValue('nome', null)
    setValue('dataNascimento', '')
    setValue('telefone', '')
    setValue('email', null)
    setValue('sexo', 0)
  }

  const handleItemValue = (name: keyof IFormDadosPaciente, item: any) => {
    setValue(name, item, {
      shouldValidate: true
    })
  }
  // useEffect(() => {
  //   obterDadosPaciente(id)
  // }, [id])
  async function obterDadosPaciente(value) {
    try {
      // if (id) {
      //   setLoading(true)
      //   const { data } = await obterDadosPacientePorId(value)
      //   mapear(data)
      //   setLoading(false)
      // }
    } catch (error) {
      showNotificationErrorAPI(error)
      setLoading(false)
    }
  }

  const handleRadios = e => {
    handleItemValue('sexo', parseInt(e.target.value))
  }

  function mapear(dadosPaciente: IFormDadosPaciente) {
    const keysForm = Object.keys(dadosPacienteDefault)
    for (const key in dadosPaciente) {
      if (keysForm.includes(key)) {
        setValue(key as any, dadosPaciente[key], { shouldValidate: true })
      }
    }
  }

  const onSubmit = async () => {
    try {
      let paciente: ISalvarDadosPacienteRequest = {
        id: command.id || null,
        nome: command.nome,
        sexo: command.sexo,
        cpf: onlyNumber(command.cpf),
        telefone: onlyNumber(command.telefone),
        dataNascimento: moment(command.dataNascimento).toJSON(),
        email: command.email
      }

      let request: IVincularPacienteSolicitacaoRequest = {
        solicitacaoId: solicitacao.id,
        paciente: paciente
      }
      if (pacienteResponsavel) {
        request.responsavelId = command.responsavelPacienteId
        request.paciente.cpfResponsavel = onlyNumber(command.cpfResponsavel)
        request.paciente.nomeResponsavel = command.nomeResponsavel
      }
      setSubmitting(true)
      var response = await vincularPacienteSolicitacao(request)
      solicitacao.status.id = response.data.statusSolicitacaoOrcamentoId
      solicitacao.pacienteId = response.data.paciente.id
      solicitacao.nomePaciente = response.data.paciente?.nome
      solicitacao.pacienteTemResponsavel =
        response.data.responsavelPaciente?.nome
      solicitacao.pacienteTemCpf = !!response.data.paciente?.cpf

      showNotification(
        'Parabéns',
        'Paciente vinculado com sucesso.',
        'success',
        true
      )
      setTimeout(() => handleAfterSubmit(true, solicitacao), 300)
      setSubmitting(false)
    } catch (error) {
      setSubmitting(false)
      showNotificationErrorAPI(error)
    }
  }

  return (
    <>
      {/* <div style={{ wordWrap: 'break-word' }}>
        Command:{JSON.stringify(command)},
        {id}
      </div> */}
      <UBox render={loading}>
        <LoadingGeral />
      </UBox>
      <UBox hidden={loading}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={classes.form}
          noValidate={true}
        >
          <UBox>
            <Grid container spacing={1}>
              <Alert severity="info">
                Caso seja menor de idade ou não possuir CPF, marque a opção
                abaixo "Paciente necessita de responsável"
              </Alert>
              <UBox>
                <Grid
                  container
                  spacing={1}
                  style={{ width: '100%' }}
                  className={classes.noMargin}
                >
                  <Grid item xs={12}>
                    <Typography
                      variant="body1"
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <Checkbox
                        checked={pacienteResponsavel}
                        onClick={() =>
                          setPacienteResponsavel(!pacienteResponsavel)
                        }
                        id="check-paciente-responsavel-config"
                      />
                      Paciente necessita de responsável
                    </Typography>
                  </Grid>
                  {pacienteResponsavel && (
                    <>
                      <Grid item xs={12} style={{ marginTop: '20px' }}>
                        <Typography variant="subtitle2">Responsável</Typography>
                        <Divider style={{ margin: '10px 0' }} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          erro={errors}
                          loading={loadingPaciente}
                          name="cpfResponsavel"
                          placeholder="Ex: 999.999.999-99"
                          label="CPF do responsável"
                          type="tel"
                          hiddenLabel
                          control={control}
                          mask="999.999.999-99"
                          InputLabelProps={{
                            shrink: true
                          }}
                          InputProps={{
                            autoComplete: 'new-password'
                          }}
                          id="input-cpf-responsavel-config"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          loading={loadingPaciente}
                          erro={errors}
                          name="nomeResponsavel"
                          label="Nome completo do responsável"
                          inputRef={register}
                          InputLabelProps={{
                            shrink: true
                          }}
                          inputProps={{ maxLength: 100 }}
                          id="input-nome-responsavel-config"
                        />
                      </Grid>
                      <Grid item xs={12} style={{ marginTop: '20px' }}>
                        <Typography variant="subtitle2">Paciente</Typography>
                        <Divider style={{ margin: '10px 0' }} />
                      </Grid>
                    </>
                  )}
                  {!pacienteResponsavel && (
                    <Grid item xs={12}>
                      <TextField
                        loading={loadingPaciente}
                        erro={errors}
                        name="cpf"
                        placeholder="Ex: 999.999.999-99"
                        label="CPF"
                        type="tel"
                        hiddenLabel
                        control={control}
                        mask="999.999.999-99"
                        InputLabelProps={{
                          shrink: true
                        }}
                        id="input-cpf-paciente-config"
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      erro={errors}
                      name="nome"
                      label="Nome completo"
                      inputRef={register}
                      InputLabelProps={{
                        shrink: true
                      }}
                      inputProps={{ maxLength: 100 }}
                      id="input-nome-paciente-config"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DateInput
                      errors={errors}
                      name="dataNascimento"
                      label="Data de nascimento"
                      value={command.dataNascimento}
                      control={control}
                      handleChange={value =>
                        handleItemValue('dataNascimento', value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      erro={errors}
                      name="telefone"
                      maskTelCel
                      value={command.telefone}
                      label="Telefone"
                      type="tel"
                      control={control}
                      InputLabelProps={{
                        shrink: true
                      }}
                      id="input-telefone-paciente-config"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      erro={errors}
                      name="email"
                      label="E-mail"
                      inputRef={register}
                      InputLabelProps={{
                        shrink: true
                      }}
                      inputProps={{ maxLength: 100 }}
                      id="input-email-paciente-config"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <RadioGroup onChange={handleRadios} value={command.sexo}>
                      <Grid container direction="row">
                        <FormControlLabel
                          control={
                            <Radio
                              value={genero.Masculino}
                              color="primary"
                              name="pacienteSexo"
                              id="radio-sexo-masculino-config"
                            />
                          }
                          label="Masculino"
                        />
                        <FormControlLabel
                          control={
                            <Radio
                              value={genero.Feminino}
                              color="primary"
                              name="pacienteSexo"
                            />
                          }
                          label="Feminino"
                        />
                      </Grid>
                    </RadioGroup>
                    <FormControl fullWidth className={classes.formControl}>
                      <FormHelperText error={!!errors.sexo}>
                        {errors.sexo?.message}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                </Grid>
              </UBox>
            </Grid>
            <Grid container justify="flex-end" style={{ marginTop: '24px' }}>
              <Grid item xs={12} sm={4}>
                <Button
                  loading={submitting}
                  disabled={submitting}
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                >
                  Salvar
                </Button>
              </Grid>
            </Grid>
          </UBox>
        </form>
      </UBox>
    </>
  )
}
